import React, { useState, useEffect, useCallback } from 'react';
import ZoomVideoSDK from '../components/video/ZoomVideoSDK';
import ScribePanel from '../components/scribe/ScribePanel';
import MobileScribeFAB from '../components/mobile/MobileScribeFAB';
import SessionManager from '../components/SessionManager';
import useResponsive from '../hooks/useResponsive';
import { getAuthData } from '../utils/sessionStorage';
import UnifiedWebSocketService from '../services/UnifiedWebSocketService';

/**
 * SolHomePage - Main provider workspace for telehealth visits
 * Orchestrates video conferencing and AI documentation in a unified interface
 */
const SolHomePage = ({ 
  sessionToJoin, 
  zoomTopic, 
  zoomPasscode, 
  miraSolCaseId, 
  coreServerCaseId, 
  providerInfo, 
  healthcareInfo 
}) => {
  const { viewport } = useResponsive();
  
  // Authentication and provider state
  const [authData, setAuthData] = useState(() => getAuthData());
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Scribe panel state
  const [soapNote, setSoapNote] = useState('');
  const [transcript, setTranscript] = useState('');
  const [speakerSegments, setSpeakerSegments] = useState([]);
  const [showMobileScribe, setShowMobileScribe] = useState(false);
  const [isScribePanelMinimized, setIsScribePanelMinimized] = useState(false);
  
  // Get initial tab from URL path
  const getInitialTab = () => {
    const validTabs = ['transcription', 'notes', 'templates', 'session', 'quanta-eval'];
    const pathTab = window.location.pathname.slice(1); // Remove leading slash
    return validTabs.includes(pathTab) ? pathTab : 'cases';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  
  // Local state for selected case (overrides the prop if a case is selected)
  const [selectedCaseId, setSelectedCaseId] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('id') || miraSolCaseId;
  });
  
  // Use selected case ID if available, otherwise use the prop
  const currentCaseId = selectedCaseId || miraSolCaseId;
  
  // Debug case ID flow
  // console.log('[SolHomePage] Debug case IDs:', {
  //   selectedCaseId,
  //   miraSolCaseId,
  //   currentCaseId,
  //   urlParams: new URLSearchParams(window.location.search).get('id')
  // });
  
  // Store current case ID in localStorage as single source of truth
  useEffect(() => {
    if (currentCaseId) {
      localStorage.setItem('currentCaseId', currentCaseId);
      // console.log('[SolHomePage] Stored case ID in localStorage:', currentCaseId);
    } else {
      localStorage.removeItem('currentCaseId');
      // console.log('[SolHomePage] Removed case ID from localStorage');
    }
  }, [currentCaseId]);
  

  // Update URL when tab changes
  const updateUrlWithTab = useCallback((tabId, caseId = null) => {
    const url = new URL(window.location.origin);
    
    // Use root path for cases, clean paths for other tabs
    if (tabId === 'cases') {
      url.pathname = '/';
    } else {
      url.pathname = `/${tabId}`;
    }
    
    // Preserve existing query parameters except 'tab'
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('tab');
    
    if (caseId) {
      searchParams.set('id', caseId);
      setSelectedCaseId(caseId);
      // Immediately update localStorage when case is selected
      localStorage.setItem('currentCaseId', caseId);
    } else {
      // Clear localStorage when no case is selected
      localStorage.removeItem('currentCaseId');
    }
    
    if (searchParams.toString()) {
      url.search = searchParams.toString();
    }
    
    window.history.pushState({}, '', url);
  }, []);

  // No redirect needed - root path shows cases tab by default

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    updateUrlWithTab(tabId);
  };

    // Sync authData state with sessionStorage when authentication changes
useEffect(() => {
  if (isAuthenticated) {
    const latestAuthData = getAuthData();
    setAuthData(latestAuthData);
  }
}, [isAuthenticated]);
  // Auth data is loaded from sessionStorage on mount via lazy initialization above
  // SessionManager will confirm authentication after biometric unlock on native

  // Listen for browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const newTab = getInitialTab();
      setActiveTab(newTab);
      
      // Also read case ID from URL when navigating
      const urlParams = new URLSearchParams(window.location.search);
      const caseIdFromUrl = urlParams.get('id');
      if (caseIdFromUrl) {
        setSelectedCaseId(caseIdFromUrl);
        // Update localStorage when navigating via browser history
        localStorage.setItem('currentCaseId', caseIdFromUrl);
      } else {
        // Clear localStorage when no case in URL
        localStorage.removeItem('currentCaseId');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Cleanup WebSocket connection when page unmounts (not when switching tabs)
  useEffect(() => {
    return () => {
      // Only cleanup when the entire page unmounts (user navigates away)
      UnifiedWebSocketService.stopAudioCapture();
      UnifiedWebSocketService.disconnect();
    };
  }, []);

  // Handle token validation from SessionManager
  const handleTokenValidated = useCallback((tokenData) => {
    if (tokenData.cleared) {
      // Token was cleared
      setAuthData(null);
      setIsAuthenticated(false);
    } else {
      // Token was validated
      setAuthData(tokenData);
      setIsAuthenticated(true);
    }
  }, []);

  // Handle provider data loaded
  const handleProviderDataLoaded = useCallback((providerData) => {
  }, []);

  // Handle authentication errors
  const handleAuthError = useCallback((error) => {
    setIsAuthenticated(false);
  }, []);

  // Get provider information for display
  const getProviderInfo = () => {
    if (authData?.provider_data) {
      return {
        name: authData.provider_data.name || authData.provider_data.first_name + ' ' + authData.provider_data.last_name,
        provider_id: authData.provider_data.id,
        id: authData.provider_data.id, // Keep both for compatibility
        healthSystem: authData.provider_data.health_system?.name
      };
    }
    return providerInfo || { name: 'Provider', provider_id: null, id: null, healthSystem: null };
  };

  const currentProviderInfo = getProviderInfo();

  return (
    <div className="sol-workspace">
      {/* Session Manager for authentication */}
      <SessionManager
        onTokenValidated={handleTokenValidated}
        onProviderDataLoaded={handleProviderDataLoaded}
        onError={handleAuthError}
      />

      {viewport.isMobile ? (
        // Mobile Layout: Full-screen video with FAB for scribe access
        <div className="mobile-workspace">
          <div className="video-container-mobile">
            <ZoomVideoSDK
              key={currentCaseId}
              sessionToJoin={sessionToJoin}
              zoomTopic={zoomTopic}
              zoomPasscode={zoomPasscode}
              miraSolCaseId={currentCaseId}
              coreServerCaseId={coreServerCaseId}
              providerInfo={currentProviderInfo}
              healthcareInfo={healthcareInfo}
              onTranscriptUpdate={setTranscript}
              onSpeakerSegmentsUpdate={setSpeakerSegments}
              onSoapNoteUpdate={setSoapNote}
            />
          </div>

          {/* Mobile Scribe FAB */}
          <MobileScribeFAB
            isOpen={showMobileScribe}
            onToggle={() => setShowMobileScribe(!showMobileScribe)}
            soapNote={soapNote}
            transcript={transcript}
            speakerSegments={speakerSegments}
            onUpdateNote={setSoapNote}
            providerInfo={currentProviderInfo}
            authData={authData}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            updateUrlWithTab={updateUrlWithTab}
            caseId={currentCaseId}
          />
        </div>
      ) : (
        // Desktop/Tablet Layout: Side-by-side video and scribe panels
        <div className={`desktop-workspace ${isScribePanelMinimized ? 'scribe-minimized' : ''}`}>
          <div className="video-panel">
            <ZoomVideoSDK
              key={currentCaseId}
              sessionToJoin={sessionToJoin}
              zoomTopic={zoomTopic}
              zoomPasscode={zoomPasscode}
              miraSolCaseId={currentCaseId}
              coreServerCaseId={coreServerCaseId}
              providerInfo={currentProviderInfo}
              healthcareInfo={healthcareInfo}
              onTranscriptUpdate={setTranscript}
              onSpeakerSegmentsUpdate={setSpeakerSegments}
              onSoapNoteUpdate={setSoapNote}
            />
          </div>

          <div className={`scribe-panel ${isScribePanelMinimized ? 'minimized' : ''}`}>
            <ScribePanel
              soapNote={soapNote}
              transcript={transcript}
              speakerSegments={speakerSegments}
              onUpdateNote={setSoapNote}
              providerInfo={currentProviderInfo}
              authData={authData}
              isAuthenticated={isAuthenticated}
              onMinimizedChange={setIsScribePanelMinimized}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              updateUrlWithTab={updateUrlWithTab}
              caseId={currentCaseId}
            />
          </div>
        </div>
      )}

    </div>
  );
};

export default SolHomePage;